import React from 'react';

/**
 * ChatMarkdown — minimal markdown for chat bubbles.
 *
 * Mira writes markdown but Silent Mode rendered it as plain text, so
 * students saw literal **asterisks** around key terms (visible in cohort
 * teacher screenshots). This renders the subset Mira actually produces —
 * bold, italics, inline code, bullet/numbered lists — as React elements
 * (no dangerouslySetInnerHTML). Anything else stays plain text, so a
 * partially-streamed token can never break the bubble.
 */

const INLINE_RE = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g;

const renderInline = (text, keyBase) => {
  const parts = [];
  let last = 0;
  let i = 0;
  let m;
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      parts.push(<strong key={`${keyBase}b${i++}`}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('`')) {
      parts.push(<code key={`${keyBase}c${i++}`}>{tok.slice(1, -1)}</code>);
    } else {
      parts.push(<em key={`${keyBase}i${i++}`}>{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const BULLET_RE = /^\s*[-•*]\s+(.*)$/;
const NUMBER_RE = /^\s*(\d+)[.)]\s+(.*)$/;

const ChatMarkdown = ({ text }) => {
  if (!text) return null;
  const lines = String(text).split('\n');
  const blocks = [];
  let listItems = null; // { ordered, items }

  const flushList = () => {
    if (!listItems) return;
    const ListTag = listItems.ordered ? 'ol' : 'ul';
    blocks.push(
      <ListTag key={`l${blocks.length}`} className="chat-md-list">
        {listItems.items.map((item, j) => (
          <li key={j}>{renderInline(item, `${blocks.length}-${j}`)}</li>
        ))}
      </ListTag>
    );
    listItems = null;
  };

  lines.forEach((line, idx) => {
    const bullet = line.match(BULLET_RE);
    const numbered = line.match(NUMBER_RE);
    if (bullet || numbered) {
      const ordered = Boolean(numbered);
      if (!listItems || listItems.ordered !== ordered) {
        flushList();
        listItems = { ordered, items: [] };
      }
      listItems.items.push(bullet ? bullet[1] : numbered[2]);
    } else {
      flushList();
      // Preserve blank lines as paragraph spacing, not empty divs
      if (line.trim() === '') {
        blocks.push(<div key={`s${idx}`} className="chat-md-space" />);
      } else {
        blocks.push(<div key={`p${idx}`}>{renderInline(line, idx)}</div>);
      }
    }
  });
  flushList();

  return <>{blocks}</>;
};

export default ChatMarkdown;
